import { RightShipDialogServiceConstants } from "../../../../../../../../constants/basics/rightship-dialog.constants";
import {
    MepDashboardRequest,
    MepEventMessages,
    MepGetDashboardVesselStatisticsRequest,
    MepTabsEnum
} from "../../../../../../../../models/mep/mep.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { MepService } from "../../../../../../../../services/mep/mep.service";
import { NotificationClientService } from "../../../../../../../../services/basics/notification-client.service";
import { computedFrom } from "aurelia-binding";
import { MepDashboardVesselDistributionViewModel } from "../../../../common-vm/mep-vessel-distribution-view-model";
import * as Enumerable from "linq";
import { MepDashboardFilter } from "../mep-dashboard-filter/mep-dashboard-filter";
import { EventAggregator } from "aurelia-event-aggregator";
import { useView } from "aurelia-framework";

export enum VesselStatisticsType {
    VesselCalls = 1,
    VesselCount = 2,
    TugboatJobs = 3
}

@useView('views/pages/modules/mep/tabs/mep-dashboard/partials/mep-vessel-statistics/mep-vessel-statistics.html')
export class MepVesselStatistics {

    private _tugboatJobsAllowedOperationMode = 'Manoeuvre';
    private _tugboatVesselTypesAllowed = 'Tugboat';

    public vesselStatisticsItems: MepVesselStatisticItemViewModel[];
    public isVesselStatisticsLoading: boolean = false;
    public getRequestDataFunc: () => MepDashboardRequest;

    public get showTugboatsVesselStatistics() {
        const tugboatVesselTypes = this.filterViewModel.selectedVesselTypeGroups.filter(x => x.name == this._tugboatVesselTypesAllowed);
        const tugboatOperationModes = this.filterViewModel.selectedOperationModes.filter(x => x.name == this._tugboatJobsAllowedOperationMode);

        return (tugboatVesselTypes.length > 0 || this.filterViewModel.selectedVesselTypeGroups.length == 0) &&
            (tugboatOperationModes.length > 0 || this.filterViewModel.selectedOperationModes.length == 0)
    }

    constructor(getRequestDataFunc: () => MepDashboardRequest, private dialogClientService: DialogClientService,
        private mepService: MepService, private notificationClientService: NotificationClientService,
        private eventAggregator: EventAggregator, private filterViewModel: MepDashboardFilter) {
        this.getRequestDataFunc = getRequestDataFunc;

    }

    getMepVesselStatistics(request: MepDashboardRequest) {
        return new Promise<string>((resolve, reject) => {
            this.isVesselStatisticsLoading = true;
            const vesselStatisticsRequest: MepGetDashboardVesselStatisticsRequest = {
                emissionTypeIds: request.emissionTypeIds,
                operationModeIds: request.operationModeIds,
                pointOfInterestIds: request.pointOfInterestIds,
                cargoTypeIds: request.cargoTypeIds,
                portId: request.portId,
                vesselTypeGroupIds: request.vesselTypeGroupIds,
                vesselTypeIds: request.vesselTypeIds,
                year: request.year,
                months: request.months,
                lastYear: request.year - 1
            };
            this.mepService.getMepVesselStatistics(vesselStatisticsRequest).then(response => {
                if (response.vesselStatistics) {
                    this.vesselStatisticsItems = [
                        new MepVesselStatisticItemViewModel(VesselStatisticsType.VesselCalls, response.vesselStatistics.vesselCalls, response.vesselStatistics.totalVesselCalls,
                            response.vesselCallsPercentage, this.getRequestDataFunc, this.dialogClientService, this.mepService, this.notificationClientService, this.filterViewModel),
                        new MepVesselStatisticItemViewModel(VesselStatisticsType.VesselCount, response.vesselStatistics.vesselCount, response.vesselStatistics.totalVesselCount,
                            response.vesselCountPercentage, this.getRequestDataFunc, this.dialogClientService, this.mepService, this.notificationClientService, this.filterViewModel),
                    ];

                    if (this.showTugboatsVesselStatistics) this.vesselStatisticsItems.push(new MepVesselStatisticItemViewModel(VesselStatisticsType.TugboatJobs, response.vesselStatistics.tugboatJobs, response.vesselStatistics.totalTugboatJobs,
                        response.tugboatJobsPercentage, this.getRequestDataFunc, this.dialogClientService, this.mepService, this.notificationClientService, this.filterViewModel))
                }
                this.isVesselStatisticsLoading = false;
                resolve(null);
            }).catch(() => {
                this.isVesselStatisticsLoading = false;
                reject("MEP Vessel Statistics - Error retrieving the vessel statistics!");
            });
        });
    }

    clearData() {
        this.vesselStatisticsItems = null;
    }

    navigateToVessels() {
        const request = this.getRequestDataFunc();
        if (request) {
            request.step = MepTabsEnum.Vessels;
            this.eventAggregator.publish(MepEventMessages.ChangeMepTab, request);
        }
    }

}

export class MepVesselStatisticItemViewModel {
    vesselStatisticType: number;
    value: number;
    totalValue: number;
    percentage: number;
    vesselDistributionViewModel: MepDashboardVesselDistributionViewModel;
    getRequestDataFunc: () => MepDashboardRequest;

    constructor(vesselStatisticType: number, value: number, totalValue: number, percentage: number, getRequestDataFunc: () => MepDashboardRequest,
        private dialogClientService: DialogClientService, private mepService: MepService, private notificationClientService: NotificationClientService,
        private filterViewModel: MepDashboardFilter) {
        this.vesselStatisticType = vesselStatisticType;
        this.value = value;
        this.totalValue = totalValue;
        this.percentage = percentage;
        this.getRequestDataFunc = getRequestDataFunc;
    }

    @computedFrom("vesselStatisticType")
    get name() {
        let name = "";
        switch (this.vesselStatisticType) {
            case VesselStatisticsType.VesselCalls:
                name = "Vessel Calls";
                break;
            case VesselStatisticsType.VesselCount:
                name = "Distinct Vessel Count";
                break;
            case VesselStatisticsType.TugboatJobs:
                name = "Tugboat Jobs";
                break;
        }
        return name;
    }

    @computedFrom("vesselStatisticType")
    get icon() {
        let icon = "";
        switch (this.vesselStatisticType) {
            case VesselStatisticsType.VesselCalls:
                icon = 'anchor';
                break;
            case VesselStatisticsType.VesselCount:
                icon = 'ship-tinner';
                break;
            case VesselStatisticsType.TugboatJobs:
                icon = 'tugboat';
                break;
        }
        return icon;
    }

    @computedFrom("singleEmissionSelectedViewModel.shouldShowSingleEmission")
    get shouldDisplayTotal() {
        return this.filterViewModel.selectedEmissionTypes.length > 0 || this.filterViewModel.selectedPointsOfInterest.length > 0 || this.filterViewModel.selectedVesselTypeGroups.length > 0 ||
            this.filterViewModel.selectedOperationModes.length > 0;
    }

    @computedFrom("percentage")
    get absPercentage() {
        if (this.percentage == null)
            return this.percentage;

        return Math.abs(this.percentage);
    }

    @computedFrom("value")
    get computedValue() {
        const manoeuvreName = 'Manoeuvre';
        const request = this.getRequestDataFunc();
        const manoeuvreItem = this.filterViewModel.operationModes.filter(x => x.name == manoeuvreName)[0]
        const manoeuvreId = manoeuvreItem ? manoeuvreItem.id : 0;
        if (this.vesselStatisticType != VesselStatisticsType.TugboatJobs || (this.vesselStatisticType == VesselStatisticsType.TugboatJobs && request && (!request.operationModeIds || request.operationModeIds.length == 0
            || Enumerable.from(request.operationModeIds).where(x => x == manoeuvreId).any())))
            return this.value ? this.value : 0;

        return 0;
    }

    shouldShowVesselDistributionButton() {
        const request = this.getRequestDataFunc();
        return this.value && this.value !== 0 && request && request.vesselTypeGroupIds && request.vesselTypeGroupIds.length != 1;
    }

    openVesselTypeDistribution(shouldOpenDialog: boolean = true) {
        this.vesselDistributionViewModel = new MepDashboardVesselDistributionViewModel(this.mepService, this.notificationClientService, this.vesselStatisticType);
        const request = this.getRequestDataFunc();
        if (request) {
            this.vesselDistributionViewModel.loadVesselTypeDistribution({
                emissionTypeIds: request.emissionTypeIds,
                operationModeIds: request.operationModeIds,
                pointOfInterestIds: request.pointOfInterestIds,
                cargoTypeIds: request.cargoTypeIds,
                portId: request.portId,
                vesselStatisticTypeId: this.vesselStatisticType,
                vesselTypeGroupIds: request.vesselTypeGroupIds,
                vesselTypeIds: request.vesselTypeIds,
                year: request.year,
                months: request.months
            });

            if (shouldOpenDialog) {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.VesselTypeDistributionDialog,
                    this, true, false, false, 'right');
            }
        }
    }
}
